<template>
  <div @click="hideStorageItemDetails" class="storage-files">
    <div class="storage__navigation">
      <AssignmentsAndFilesNavigation
        :isShowAdminFolders="isShowAdminFolders"
        :setIsShowAdminFoldersRootLevel="handleSetIsShowAdminFoldersRootLevel"
        :handleSearchingInfoShow="handleSearchingInfoShow"
      >
        <StorageBreadcrumbs v-if="breadcrumbs" :breadcrumbs="breadcrumbs" />
      </AssignmentsAndFilesNavigation>

      <div class="d-flex flex-column">
        <div
          :class="[
            'd-flex',
            isRenderStorageUploadButtons
              ? 'justify-space-between'
              : 'justify-end'
          ]"
        >
          <div class="d-flex">
            <div
              v-if="isRenderStorageUploadButtons"
              class="d-flex add-storage-item-buttons"
            >
              <CommonButton
                :label="$t('upload_file.upload_files')"
                customClassName="mr-3"
                @handleClick="handleToggleUploadDialog"
                width="130"
                :disabled="isAddStorageItemButtonDisabled"
              />
              <AddStorageItemMenu
                :isMenuDisabled="isAddStorageItemButtonDisabled"
                :limitedFunctionality="limitedFunctionality"
              />
            </div>
            <v-switch
              v-if="isRenderAdminFoldersSwitch"
              class="mt-0 ml-5"
              v-model="isShowAdminFolders"
              :disabled="isStorageContentLoading"
            >
              <template v-slot:label>
                <span
                  class="storage-files__admin-folders-switch secondary--text font-weight-medium"
                  >{{ $t("storage.show_admin_folders") }}</span
                >
              </template>
            </v-switch>
          </div>
          <div class="d-flex storage-files__search">
            <form @submit.prevent="$emit('handleSubmit')">
              <StorageSearchInput
                :fileName="fileName"
                :handleSearchingInfoShow="handleSearchingInfoShow"
                :handleFileNameChange="handleFileNameChange"
                :selectedTags="selectedTags"
                :handleSelectedTagsChange="handleSelectedTagsChange"
              />
            </form>
          </div>
        </div>
      </div>
    </div>
    <StorageList
      class="storage-list"
      :setIsShowAdminFoldersRootLevel="handleSetIsShowAdminFoldersRootLevel"
      :isShowSearchingInfo="isShowSearchingInfo"
      :handleSearchingInfoShow="handleSearchingInfoShow"
      :handleFileNameChange="handleFileNameChange"
      :handleSelectedTagsChange="handleSelectedTagsChange"
    />
  </div>
</template>

<script>
import StorageSearchInput from "../form/StorageSearchInput";
import StorageList from "../StorageList";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ModuleTypes } from "@/store/ModuleTypes";
import { Routes } from "@/router/Routes";
import CommonButton from "@/views/academy/common/CommonButton";
import AddStorageItemMenu from "@/views/academy/storage/files/AddStorageItemMenu";
import { generateBreadcrumbs } from "@/helpers/generateBreadcrumbs";
import StorageBreadcrumbs from "@/views/academy/storage/files/StorageBreadcrumbs";
import { isAdminRole } from "@/helpers/userRole.utils";
import AssignmentsAndFilesNavigation from "@/views/academy/common/AssignmentsAndFilesNavigation";
import { ShareStorageItemUserTypes } from "@/views/academy/enums/ShareStorageItemUserTypes";
import GetterTypes from "@/store/GetterTypes";
import { bus } from "@/main";

export default {
  name: "StorageFilesPage",
  props: {
    searchValue: {
      type: String,
      required: true
    },
    isShowAdminFoldersCallback: {
      type: Boolean,
      required: true
    },
    toggleShowAdminFolders: {
      type: Function,
      required: true
    }
  },
  components: {
    AssignmentsAndFilesNavigation,
    StorageBreadcrumbs,
    AddStorageItemMenu,
    CommonButton,
    StorageSearchInput,
    StorageList
  },
  data: () => ({
    ACADEMY_STORAGE_ORGANIZATION: Routes.ACADEMY_STORAGE_ORGANIZATION.path,
    isShowAdminFolders: false,
    isShowAdminFoldersRootLevel: false,
    isShowSearchingInfo: false,
    fileName: "",
    selectedTags: []
  }),
  computed: {
    ...mapGetters(ModuleTypes.AUTH, ["userRoles"]),
    ...mapGetters(ModuleTypes.STORAGE, [
      "storageContent",
      "activeOrganization",
      "activePlayer",
      "isUploadFilesDialogOpened",
      "storageSearchInputValue",
      "levels",
      "storageItemDetails",
      "isStorageContentLoading"
    ]),
    ...mapGetters({
      limitedFunctionality: GetterTypes.GET_ORG_HAS_LIMITED_FUNCTIONALITY
    }),

    isAdminRole: function() {
      return isAdminRole(this.userRoles);
    },

    renderFilesRoute: function() {
      if (this.isAdminRole) {
        return `${Routes.ACADEMY_STORAGE_ORGANIZATION.path}/${this.activeOrganization?.id}`;
      } else {
        return `${Routes.ACADEMY_STORAGE_ORGANIZATION.path}/${this.activeOrganization?.id}/player/${this.activePlayer?.playerId}`;
      }
    },

    breadcrumbs: function() {
      return generateBreadcrumbs(
        this.storageContent?.breadcrumbs,
        this.activeOrganization?.id || this.activePlayer?.organizationId,
        this.activePlayer?.playerId,
        this.isAdminRole
      );
    },

    isRenderStorageUploadButtons() {
      return !this.$route.params?.playerId;
    },

    isAddStorageItemButtonDisabled() {
      const { accessType, virtual, breadcrumbs } = this.storageContent;
      const hasAccessToUploadStorageItem = !(
        accessType === null ||
        accessType === ShareStorageItemUserTypes.VIEWER ||
        (virtual && !this.levels?.organizationLevel) ||
        (this.isShowAdminFoldersRootLevel && this.isShowAdminFolders) ||
        (breadcrumbs &&
          breadcrumbs[breadcrumbs?.length - 1]?.sharedWithMe &&
          virtual)
      );
      this.setAccessToUploadStorageItem({ hasAccessToUploadStorageItem });
      return !hasAccessToUploadStorageItem;
    },

    isRenderAdminFoldersSwitch() {
      return (
        this.storageContent?.virtual &&
        this.levels?.organizationLevel &&
        this.storageContent?.breadcrumbs?.length === 0
      );
    },

    isRenderStorageSearch() {
      return !this.storageContent?.virtual;
    }
  },

  methods: {
    ...mapMutations(ModuleTypes.STORAGE, [
      "toggleUploadFilesDialog",
      "setStorageSearchInputValue",
      "setStorageSearchInputFocused",
      "setIsAdminFoldersRootLevel",
      "setAccessToUploadStorageItem"
    ]),
    ...mapActions(ModuleTypes.STORAGE, [
      "hideStorageItemDetails",
      "fetchStorageContent"
    ]),
    ...mapMutations(ModuleTypes.ASSIGNMENTS, ["setAssignmentItemDetails"]),

    handleToggleUploadDialog() {
      if (this.limitedFunctionality) {
        bus.$emit("showExpiredSubscription");
      } else {
        this.toggleUploadFilesDialog({ isUploadFilesDialogOpened: true });
      }
    },

    handleSetIsShowAdminFoldersRootLevel(value) {
      this.setIsAdminFoldersRootLevel({ isAdminFoldersRootLevel: value });
      this.isShowAdminFoldersRootLevel = value;
    },
    handleSearchingInfoShow(value) {
      this.isShowSearchingInfo = value;
    },
    handleFileNameChange(value) {
      this.fileName = value;
    },

    handleSelectedTagsChange(value) {
      this.selectedTags = value;
    }
  },
  mounted() {
    this.setAssignmentItemDetails(null);
  },
  watch: {
    isShowAdminFolders(newValue) {
      this.toggleShowAdminFolders(newValue);
      this.setIsAdminFoldersRootLevel({ isAdminFoldersRootLevel: newValue });
      if (newValue) {
        this.isShowAdminFoldersRootLevel = true;
      }
      const breadcrumbs = this.storageContent.breadcrumbs;
      this.fetchStorageContent({
        organizationId: this.$route.params?.orgId,
        parentId: breadcrumbs?.[breadcrumbs?.length - 1]?.id,
        orgAdmins: newValue,
        sharedWithMe: this.storageItemDetails?.sharedWithMe
      });
    },
    isShowAdminFoldersCallback(newValue) {
      this.isShowAdminFolders = newValue;
    }
  }
};
</script>

<style>
.storage-files {
  position: relative;
  height: 100%;
}

.sidebar-left--closed + .content .storage__navigation {
  left: 80px;
}

.storage__navigation-breadcrumbs {
  border-bottom: 1px solid var(--divider-color);
  padding-bottom: 8px;
  margin-bottom: 16px;
}

.sidebar-list__title {
  font-size: 0.7rem !important;
}

.outline-none {
  outline: none;
}

.storage-files__admin-folders-switch {
  font-size: 0.9rem;
}

.storage-files__search {
  position: absolute;
  right: 0;
  top: initial;
}
@media (max-width: 676px) {
  .storage-files__search {
    flex-basis: 100%;
  }
}
@media (max-width: 600px) {
  .add-storage-item-buttons {
    position: absolute;
    left: 2vh;
    top: 20vh;
    z-index: 1;
  }

  .storage-files__search {
    left: 2vh;
  }
}
@media (max-width: 600px) {
  .add-storage-item-buttons {
    position: absolute;
    left: 0;
    top: 16vh;
  }
}
@media (max-width: 360px) {
  .storage-files__search {
    left: 0;
  }
}
</style>
